import { Button } from "../../components/ui/button";
import { gluceraAuthCta } from "../../utils/gluceraTypography";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useGoogleLogin, TokenResponse } from "@react-oauth/google";
import { useState, useEffect, useRef } from "react";
import {
  extractAuthTokens,
  isAuthLoginSuccess,
  saveGluceraAuthSession,
} from "../../utils/authTokens";
import assessmentStatusService from "../../services/AssessmentStatusService";
import BrandLogo from "../../components/BrandLogo";
import welcomeBg from "../../assets/homebg.png";
import { post } from "@/services/axiosUitils";
import fitbitAuthService from "../../services/FitbitAuthService";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import SessionStorageHelper from "../../utils/sessionStorageHelper";

interface GoogleLoginResponse {
  success?: boolean | { code?: string; message?: string };
  message: string;
  user: {
    id: string;
    username: string;
    email: string;
    full_name: string;
    first_name: string;
    last_name: string;
    gender: string;
    date_of_birth: string | null;
    is_email_verified: boolean;
    user_type: string;
  };
  tokens?: {
    access: string;
    refresh: string;
  };
  provider: string;
}

const OnbordingLoginpage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const hasExchangedFitbit = useRef(false);

  // Fitbit redirect: iframe modal / popup me postMessage, same tab me callback API + navigate
  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");
    if (!code || !state) return;
    if (window.parent !== window) {
      window.parent.postMessage(
        { type: "FITBIT_CALLBACK", code, state, success: true },
        "*"
      );
      return;
    }
    if (window.opener) {
      window.opener.postMessage(
        { type: "FITBIT_CALLBACK", code, state, success: true },
        window.location.origin
      );
      window.close();
      return;
    }

    if (hasExchangedFitbit.current) return;
    hasExchangedFitbit.current = true;

    // Clear search parameters from URL immediately to prevent other components/renders from reacting
    const newUrl = window.location.pathname;
    window.history.replaceState({}, document.title, newUrl);

    (async () => {
      try {
        await fitbitAuthService.exchangeCode(code, state);
        navigate(`/glucera${GluceraNavigationRoutes.FITNESS}`, { replace: true });
      } catch {
        navigate(`/glucera${GluceraNavigationRoutes.fitbitConnectionPage}`, {
          replace: true,
          state: { error: "Failed to connect Fitbit. Please try again." },
        });
      }
    })();
  }, [searchParams, navigate]);

  useEffect(() => {
    // Override body background for this page
    document.body.style.background = "transparent";
    document.body.style.backgroundColor = "transparent";

    return () => {
      // Restore default background when leaving
      document.body.style.background = "";
      document.body.style.backgroundColor = "";
    };
  }, []);

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse: TokenResponse) => {
      setIsLoading(true);
      setError(null);
      try {
        const payload = {
          access_token: tokenResponse.access_token,
          authuser: "1",
          expires_in: "3599",
          scope:
            "email profile https://www.googleapis.com/auth/userinfo.email openid https://www.googleapis.com/auth/userinfo.profile",
          token_type: "Bearer",
        };

        const result = await post<GoogleLoginResponse>(
          "/auth/staticGoogleLogin/",
          payload
        );

        const data = result;

        const tokens = extractAuthTokens(data);
        if (isAuthLoginSuccess(data) && tokens) {
          saveGluceraAuthSession(tokens, data.user);

          try {
            assessmentStatusService.clearCache();
            const assessmentStatus =
              await assessmentStatusService.checkAssessmentCompletion(
                tokens.access,
                { forceRefresh: true }
              );

            // Preserve family_invite_token if it exists in URL
            const familyInviteToken = searchParams.get("family_invite_token");
            const queryString = familyInviteToken
              ? `?family_invite_token=${familyInviteToken}`
              : "";

            if (assessmentStatus.isComplete) {
              // Persist the completion gate so the protected route short-circuits
              // and does NOT re-call the assessment API right after login.
              localStorage.setItem("glucera_onboarding_complete", "true");
              navigate("/glucera/homepage" + queryString, { replace: true });
            } else {
              // Clear any stale flag or assessment data from a previous user.
              localStorage.removeItem("glucera_onboarding_complete");
              SessionStorageHelper.clearData();
              navigate("/glucera/genderpage" + queryString, { replace: true });
            }
          } catch (_assessmentError) {
            // We could not determine whether this user has completed the
            // assessment (network/server problem, even after retries). Do NOT
            // guess: sending a new user to home or a returning user back
            // through onboarding are both wrong. Ask them to retry instead.
            setError(
              "We couldn't verify your account right now. Please check your connection and try again."
            );
          }
        } else {
          setError(data?.message || "Failed to authenticate with Google");
        }
      } catch (_err: any) {
        console.error("Google OAuth error:", {
          error: _err,
          response: _err?.response?.data,
          status: _err?.response?.status,
        });
        setError(
          _err?.response?.data?.message ||
          "Unable to connect to server. Please check your internet connection and try again."
        );
      } finally {
        setIsLoading(false);
      }
    },
    onError: error => {
      console.error("Google sign in error:", error);
      setError("Google sign in was unsuccessful. Please try again.");
      setIsLoading(false);
    },
  });
  return (
    <div className="h-screen w-screen overflow-hidden">
      <div className="relative mx-auto flex h-full w-full max-w-md flex-col items-center justify-center overflow-hidden bg-black p-4 sm:p-6">
        {/* Blurred Background Image */}
        <div
          className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-sm"
          style={{ backgroundImage: `url(${welcomeBg})` }}
        />

        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/50" />

        <div className="relative z-10 w-full space-y-6 sm:space-y-8">
          <BrandLogo variant="hero" />

          {/* Form */}
          <div className="space-y-4 rounded-3xl border border-white/20 bg-white/10 p-4 backdrop-blur-md sm:space-y-6 sm:p-6">
            <div className="text-center">
              <h2 className="mb-1 text-xl font-bold text-white sm:mb-2 sm:text-2xl">
                Welcome
              </h2>
              <p className="text-xs text-white/80 sm:text-sm">
                Sign in to continue your health journey
              </p>
            </div>

            <div className="space-y-3 sm:space-y-4">
              <Button
                onClick={() => login()}
                disabled={isLoading}
                className={gluceraAuthCta}
              >
                {isLoading ? "Authenticating..." : "Login With Google"}
              </Button>

              {error && (
                <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-center">
                  <p className="text-sm font-medium text-destructive">
                    {error}
                  </p>
                </div>
              )}
            </div>
          </div>

          <p className="text-center text-xs font-medium text-white">
            By continuing, you agree to our Terms of Service and{" "}
            <Link
              to={`/glucera${GluceraNavigationRoutes.PRIVACY_POLICY}`}
              className="underline underline-offset-2 hover:text-white/80"
            >
              Privacy Policy
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OnbordingLoginpage;
